import { useEffect, useState } from "react";

import {
  Box,
  Checkbox,
  Typography,
  Button,
  Stack,
  TextField,
  FormControlLabel,
  FormGroup,
  RadioGroup,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";

import useSurvey from "../../hooks/useSurvey";
import useBrowser from "../../hooks/useBrowser";
import usePage from "../../hooks/usePage";
import { includeControllerQuestions } from "../../utils/surveyCopyLogic";

import EditorToolbar from "../editors/EditorToolbar";
import Loading from "../ui/Loading";
import MUIIcon from "./MUIIcon";
import Select from "../elements/Select";

const SurveyCopy = ({ surveyId, onSave }) => {
  const theme = useTheme();
  const { windowHeight } = useBrowser();
  const { showMessage } = usePage();
  const {
    getById,
    survey,
    getSurveysForClient,
    getAllSurveys,
    surveys,
    copySurvey,
    saveCopy,
  } = useSurvey();

  // Copy target
  const [copyTo, setCopyTo] = useState("current");
  const [newSurveyName, setNewSurveyName] = useState("");

  // Copy options
  const [copyOptions, setCopyOptions] = useState({
    includeSkus: true,
    includeVariability: true,
    includeAvailability: true,
    includeControlledBy: true,
    includeVisibility: true,
    includeDescriptions: true,
  });

  // All surveys toggle
  const [showAllSurveys, setShowAllSurveys] = useState(false);
  const [isCrossClient, setIsCrossClient] = useState(false);
  const [expandedOptions, setExpandedOptions] = useState(true);

  // Selection state
  const [openPages, setOpenPages] = useState([]);
  const [openSections, setOpenSections] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [newSurvey, setNewSurvey] = useState(null);

  useEffect(() => {
    if (!surveyId) {
      return;
    }

    getData();
  }, [surveyId]);

  const getData = async () => {
    await getById(surveyId);
  };

  useEffect(() => {
    if (!survey) return;
    setSelectedSurvey(survey);
    // Don't populate newSurvey with full survey - let user select what to copy first
    setNewSurvey({ pages: [], sections: [], questions: [] });

    getSurveysForClient(survey.client.id);
  }, [survey]);

  // Handle show all surveys toggle
  useEffect(() => {
    if (showAllSurveys && getAllSurveys) {
      getAllSurveys();
    } else if (!showAllSurveys && survey && survey.client) {
      getSurveysForClient(survey.client.id);
    }
  }, [showAllSurveys]);

  // Detect cross-client when source survey changes
  useEffect(() => {
    if (!selectedSurvey || !survey) {
      setIsCrossClient(false);
      return;
    }

    const sourceClientId = selectedSurvey.client?.id;
    const targetClientId = survey.client?.id;
    const crossClient = sourceClientId !== targetClientId;

    setIsCrossClient(crossClient);

    // Auto-disable SKU option if cross-client
    if (crossClient) {
      setCopyOptions((prev) => ({ ...prev, includeSkus: false }));
    }
  }, [selectedSurvey, survey]);

  const handleOpenPage = (surveyPageId) => {
    setOpenPages((current) => {
      let opened = [...current];
      const index = opened.findIndex((pageId) => pageId === surveyPageId);
      if (index > -1) {
        opened.splice(index, 1);
      } else {
        opened.push(surveyPageId);
      }

      return opened;
    });
  };

  const handleOpenSection = (surveySectionId) => {
    setOpenSections((current) => {
      let opened = [...current];
      const index = opened.findIndex(
        (sectionId) => sectionId === surveySectionId,
      );
      if (index > -1) {
        opened.splice(index, 1);
      } else {
        opened.push(surveySectionId);
      }

      return opened;
    });
  };

  const handlePageSelection = (surveyPage) => {
    setSelectedPages((current) => {
      let selected = [...current];
      const index = selected.findIndex((page) => page.id === surveyPage.id);
      if (index > -1) {
        selected.splice(index, 1);
      } else {
        selected.push(surveyPage);
        unSelectSections(surveyPage.id);
        unSelectQuestions("surveyPageId", surveyPage.id);
      }

      return selected;
    });
  };

  const unSelectSections = (pageId) => {
    setSelectedSections((current) => {
      let selected = [];

      for (const section of current) {
        if (section.surveyPageId === pageId) {
          unSelectQuestions("surveySectionId", section.id);
          continue;
        }

        selected.push(section);
      }

      return selected;
    });
  };

  const unSelectQuestions = (field, value) => {
    setSelectedQuestions((current) => {
      let selected = [];

      for (const question of current) {
        if (field === "surveyPageId") {
          const section = selectedSurvey.sections.find(
            (s) => s.id === question.surveySectionId,
          );
          if (section && section.surveyPageId === value) {
            continue;
          }
        } else {
          if (question[field] === value) continue;
        }

        selected.push(question);
      }

      return selected;
    });
  };

  const handleSectionSelection = (surveySection) => {
    setSelectedSections((current) => {
      let selected = [...current];
      const index = selected.findIndex(
        (section) => section.id === surveySection.id,
      );
      if (index > -1) {
        selected.splice(index, 1);
      } else {
        selected.push(surveySection);
        unSelectQuestions("surveySectionId", surveySection.id);
      }

      return selected;
    });
  };

  const handleQuestionSelection = (surveyQuestion) => {
    setSelectedQuestions((current) => {
      let selected = [...current];
      const index = selected.findIndex(
        (question) => question.id === surveyQuestion.id,
      );
      if (index > -1) {
        selected.splice(index, 1);
      } else {
        selected.push(surveyQuestion);
      }

      return selected;
    });
  };

  const disableSections = (pageId) => {
    const pageSelected = selectedPages.findIndex((page) => page.id === pageId);
    if (pageSelected === -1) return false;
    return true;
  };

  const disableQuestions = (sectionId) => {
    const sectionSelected = selectedSections.findIndex(
      (section) => section.id === sectionId,
    );
    if (sectionSelected === -1) return false;
    return true;
  };

  const handleSelectSurvey = (surveyId) => {
    const selection = surveys.find((s) => s.id === surveyId);

    setSelectedSurvey(selection);
    setSelectAll(false);
    setSelectedPages([]);
    setSelectedSections([]);
    setSelectedQuestions([]);
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedPages(() => {
        return selectedSurvey.pages.map((page) => page);
      });
      setSelectedSections([]);
      setSelectedQuestions([]);
    } else {
      setSelectedPages([]);
      setSelectedSections([]);
      setSelectedQuestions([]);
    }
  };

  const pageSelected = (pageId) => {
    if (selectedPages.findIndex((page) => page.id === pageId) === -1) {
      return false;
    }
    return true;
  };

  const sectionSelected = (sectionId) => {
    if (
      selectedSections.findIndex((section) => section.id === sectionId) === -1
    ) {
      return false;
    }
    return true;
  };

  const questionSelected = (questionId) => {
    if (
      selectedQuestions.findIndex((question) => question.id === questionId) ===
      -1
    ) {
      return false;
    }
    return true;
  };

  const handleCopy = () => {
    const results = copySurvey(
      selectedSurvey,
      selectedPages,
      selectedSections,
      selectedQuestions,
      copyTo === "current" ? survey : null,
    );

    setNewSurvey(results);
  };

  const clearCopy = () => {
    // Clear to empty state instead of resetting to full source
    setNewSurvey({ pages: [], sections: [], questions: [] });
    setSelectedPages([]);
    setSelectedSections([]);
    setSelectedQuestions([]);
    setSelectAll(false);
  };

  const handleSave = async () => {
    // Validation
    if (copyTo === "new" && !newSurveyName.trim()) {
      showMessage("Please enter a survey name", "error");
      return;
    }

    if (
      selectedPages.length === 0 &&
      selectedSections.length === 0 &&
      selectedQuestions.length === 0
    ) {
      showMessage("Please select items to copy", "error");
      return;
    }

    let pagesToCopy = [...selectedPages];
    let sectionsToCopy = [...selectedSections];
    let questionsToCopy = [...selectedQuestions];

    if (copyOptions.includeControlledBy && selectedSurvey) {
      const closure = includeControllerQuestions({
        selectedSurvey,
        selectedPages,
        selectedSections,
        selectedQuestions,
      });

      pagesToCopy = closure.pagesToCopy;
      sectionsToCopy = closure.sectionsToCopy;
      questionsToCopy = closure.questionsToCopy;

      if (closure.autoAddedControllers > 0) {
        const previewNames = (closure.autoAddedControllerNames || [])
          .slice(0, 3)
          .join(", ");
        const moreCount = Math.max(
          (closure.autoAddedControllerNames || []).length - 3,
          0,
        );
        const details = previewNames
          ? ` (${previewNames}${moreCount > 0 ? ` +${moreCount} more` : ""})`
          : "";

        showMessage(
          `Auto-included ${closure.autoAddedControllers} controller question(s) to preserve conditional logic${details}.`,
          "info",
        );
      }
    }

    await saveCopy(
      selectedSurvey,
      pagesToCopy,
      sectionsToCopy,
      questionsToCopy,
      copyTo === "current" ? survey : null,
      newSurveyName,
      copyOptions,
    );

    onSave();
  };

  if (!surveyId) {
    return <Typography>Survey not available</Typography>;
  }

  if (!selectedSurvey) return <Loading />;

  return (
    <>
      <EditorToolbar onSave={handleSave} />
      <Box
        sx={{
          height: `${windowHeight - 140}px`,
          overflow: "auto",
          padding: "24px",
          display: "flex",
          flexDirection: "column",
        }}
        gap={2}
      >
        {/* Copy Target Selection */}
        <Box sx={{ borderBottom: "1px solid #ccc", paddingBottom: "16px" }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: "bold", marginBottom: "8px" }}
          >
            Copy To:
          </Typography>
          <RadioGroup
            value={copyTo}
            onChange={(e) => setCopyTo(e.target.value)}
          >
            <FormControlLabel
              value="current"
              control={<Radio size="small" />}
              label="Current Survey"
            />
            <FormControlLabel
              value="new"
              control={<Radio size="small" />}
              label="New Survey"
            />
          </RadioGroup>

          {copyTo === "new" && (
            <TextField
              label="New Survey Name"
              value={newSurveyName}
              onChange={(e) => setNewSurveyName(e.target.value)}
              required
              fullWidth
              size="small"
              sx={{ marginTop: "8px" }}
              placeholder="Enter survey name"
            />
          )}
        </Box>

        {/* Copy Options - Collapsible */}
        <Accordion
          expanded={expandedOptions}
          onChange={(e, isExpanded) => setExpandedOptions(isExpanded)}
          sx={{ border: "1px solid #ccc" }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: "bold" }}>Copy Options</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup size="small">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={copyOptions.includeSkus}
                    onChange={(e) =>
                      setCopyOptions((prev) => ({
                        ...prev,
                        includeSkus: e.target.checked,
                      }))
                    }
                    disabled={isCrossClient}
                  />
                }
                label={
                  isCrossClient
                    ? "Include SKUs (disabled - cross-client)"
                    : "Include SKU Lists"
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={copyOptions.includeVariability}
                    onChange={(e) =>
                      setCopyOptions((prev) => ({
                        ...prev,
                        includeVariability: e.target.checked,
                      }))
                    }
                  />
                }
                label="Include Variability Configuration"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={copyOptions.includeAvailability}
                    onChange={(e) =>
                      setCopyOptions((prev) => ({
                        ...prev,
                        includeAvailability: e.target.checked,
                      }))
                    }
                  />
                }
                label="Include Availability Date Ranges"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={copyOptions.includeControlledBy}
                    onChange={(e) =>
                      setCopyOptions((prev) => ({
                        ...prev,
                        includeControlledBy: e.target.checked,
                      }))
                    }
                  />
                }
                label="Include Conditional Logic (show/hide)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={copyOptions.includeVisibility}
                    onChange={(e) =>
                      setCopyOptions((prev) => ({
                        ...prev,
                        includeVisibility: e.target.checked,
                      }))
                    }
                  />
                }
                label="Include Visibility Filters"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={copyOptions.includeDescriptions}
                    onChange={(e) =>
                      setCopyOptions((prev) => ({
                        ...prev,
                        includeDescriptions: e.target.checked,
                      }))
                    }
                  />
                }
                label="Include Descriptions"
              />
            </FormGroup>
          </AccordionDetails>
        </Accordion>

        {/* Main Content Area */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flex: 1,
            overflow: "hidden",
          }}
        >
          {/* Source Survey Selection */}
          <Box
            sx={{ width: "500px", display: "flex", flexDirection: "column" }}
          >
            <Box sx={{ marginBottom: "8px" }}>
              <Select
                label="Source Survey"
                value={selectedSurvey.id}
                items={surveys.map((s) => {
                  return { id: s.id, name: s.name };
                })}
                onChange={(newValue) => handleSelectSurvey(newValue)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showAllSurveys}
                    onChange={(e) => setShowAllSurveys(e.target.checked)}
                  />
                }
                label="Show surveys from all clients"
              />
            </Box>

            {/* Selection Tree */}
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "10px",
                overflow: "auto",
                flex: 1,
                padding: "8px",
              }}
            >
              {Array.isArray(selectedSurvey.pages) &&
                selectedSurvey.pages.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      size="small"
                      checked={selectAll}
                      onChange={(e, checked) => handleSelectAll(checked)}
                    />
                    <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      Copy All
                    </Typography>
                  </Box>
                )}

              {selectedSurvey.pages.map((page) => (
                <Box key={page.id}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Checkbox
                        size="small"
                        checked={pageSelected(page.id)}
                        onChange={(e, checked) => handlePageSelection(page)}
                      />
                      <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {page.name}
                      </Typography>
                    </Box>
                    <MUIIcon
                      size="small"
                      name={
                        openPages.includes(page.id)
                          ? "ArrowCircleUp"
                          : "ArrowCircleDown"
                      }
                      onClick={() => handleOpenPage(page.id)}
                    />
                  </Box>

                  {openPages.includes(page.id) ? (
                    <Box sx={{ marginLeft: "30px" }}>
                      {selectedSurvey.sections
                        .filter((section) => section.surveyPageId === page.id)
                        .map((section) => (
                          <Box key={section.id}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={sectionSelected(section.id)}
                                  onChange={(e, checked) =>
                                    handleSectionSelection(section)
                                  }
                                  disabled={disableSections(page.id)}
                                />
                                <Typography sx={{ fontSize: "12px" }}>
                                  {section.name}
                                </Typography>
                              </Box>
                              <MUIIcon
                                size="small"
                                name={
                                  openSections.includes(section.id)
                                    ? "ArrowCircleUp"
                                    : "ArrowCircleDown"
                                }
                                onClick={() => handleOpenSection(section.id)}
                              />
                            </Box>

                            {openSections.includes(section.id) ? (
                              <Box>
                                {selectedSurvey.questions
                                  .filter(
                                    (question) =>
                                      question.surveySectionId === section.id,
                                  )
                                  .map((question) => (
                                    <Box key={question.id}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          marginLeft: "30px",
                                        }}
                                      >
                                        <Checkbox
                                          size="small"
                                          checked={questionSelected(
                                            question.id,
                                          )}
                                          onChange={(e, checked) =>
                                            handleQuestionSelection(question)
                                          }
                                          disabled={
                                            disableQuestions(section.id) ||
                                            disableSections(page.id)
                                          }
                                        />
                                        <Typography sx={{ fontSize: "10px" }}>
                                          {question.name}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ))}
                              </Box>
                            ) : null}
                          </Box>
                        ))}
                    </Box>
                  ) : null}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Copy/Reset Buttons */}
          <Box
            sx={{
              width: "120px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              backgroundColor: theme.palette.primary.light,
            }}
          >
            <Stack
              spacing={1}
              sx={{ padding: "8px" }}
            >
              <Button
                variant="contained"
                sx={{ width: "100%" }}
                onClick={handleCopy}
              >
                Copy
              </Button>
              <Button
                variant="contained"
                sx={{ width: "100%" }}
                onClick={clearCopy}
              >
                Reset
              </Button>
            </Stack>
          </Box>

          {/* Preview Panel */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              minHeight: 0,
            }}
          >
            {Array.isArray(newSurvey.pages) && newSurvey.pages.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <Typography sx={{ fontWeight: "bold", marginBottom: "8px" }}>
                  {copyTo === "current" ? "Current Survey" : "New Survey"}{" "}
                  Preview
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    padding: "8px",
                    overflow: "auto",
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  {newSurvey.pages.map((page) => (
                    <Box
                      key={page.id}
                      sx={{ marginTop: "10px" }}
                    >
                      <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {page.name}
                      </Typography>
                      <Box>
                        {newSurvey.sections
                          .filter((section) => section.surveyPageId === page.id)
                          .map((section) => (
                            <Box
                              key={section.id}
                              sx={{ marginLeft: "24px", marginTop: "5px" }}
                            >
                              <Typography sx={{ fontSize: "12px" }}>
                                {section.name}
                              </Typography>
                              <Box>
                                {newSurvey.questions
                                  .filter(
                                    (question) =>
                                      question.surveySectionId === section.id,
                                  )
                                  .map((question) => (
                                    <Box
                                      key={question.id}
                                      sx={{ marginLeft: "24px" }}
                                    >
                                      <Typography sx={{ fontSize: "10px" }}>
                                        {question.name}
                                      </Typography>
                                    </Box>
                                  ))}
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Typography>
                {copyTo === "current"
                  ? "Current survey is empty"
                  : "Preview will appear here"}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SurveyCopy;
